/**
 * Lightweight, line-based syntax highlighting for the "live edit" surface
 * (`LiveEditor.tsx`) — NOT the same parser as the authoritative
 * `DjotContent`/`AsciiDocContent` renderers used for the published view.
 * This is a fast, approximate classifier whose only job is to make typed
 * markup *look* roughly like its rendered form while the user is still
 * editing it — bold bold, headings emphasized, code tinted, etc.
 *
 * ## Every class here MUST preserve text metrics
 *
 * `LiveEditor` works by laying a styled, non-interactive backdrop
 * *exactly* behind a transparent `<textarea>`. The caret the user sees
 * belongs to the textarea; the text they see belongs to the backdrop.
 * They only appear to be the same thing while both layers lay text out
 * identically — same font, same size, same line height, same wrap
 * points.
 *
 * So no style emitted by this module may change layout geometry. In
 * particular, **never** emit:
 *
 *  - font-size (`text-lg`, `text-[0.85em]`, …) — changes glyph advance
 *    *and* line height, so the caret drifts both across and down.
 *  - line-height (`leading-7`, …) — the worst offender: it desynchronizes
 *    by a fixed amount on *every* line, so error accumulates downward and
 *    the caret ends up visibly above/below the text it belongs to.
 *  - padding/margin/border on a line (`pl-4`, `border-l-4`, …) or
 *    horizontal padding on an inline span (`px-1`) — shifts the text and
 *    moves where lines wrap, which the textarea knows nothing about.
 *  - `vertical-align` (`align-super`/`align-sub`) — can grow the line box.
 *  - `text-align` (`text-center`) — repositions the whole line.
 *  - `white-space` overrides (`whitespace-pre`) — changes wrapping.
 *  - a different `font-family` than the shared monospace stack.
 *
 * What IS safe, and all this module now uses: `color`, `font-weight`,
 * `font-style`, `text-decoration`, `background-color`, and
 * `border-radius`. In a real monospace family, bold and italic keep the
 * same advance width, so those are metric-preserving in practice.
 *
 * This is why headings here are only bolded rather than enlarged, lists
 * aren't indented, and blockquotes have no left rule: fidelity to the
 * rendered output is deliberately traded away for a caret that lands
 * where the user is actually typing. The authoritative "how will this
 * look published" view is the split editor's read-only preview.
 *
 * ## Lossless tokenization
 *
 * Markup delimiters (`*`, `_`, `` ` ``, `#`/`=` markers, list markers, …)
 * are always kept as their own de-emphasized token (`MARKER_CLASS`),
 * separate from the content they wrap, which gets the actual visual
 * style. Every character of the source stays present in the DOM, split
 * across tokens — `tokens.map(t => t.text).join('')` for a line always
 * equals that line's text — so the surface round-trips losslessly and
 * the backdrop can never disagree with the textarea about *what* text
 * exists, only about how it's painted.
 *
 * Because it's regex/line-based rather than a real parser, it can produce
 * false positives on ambiguous input (e.g. `snake_case_name` could be
 * mistaken for italics) — an accepted tradeoff for "style hints while
 * typing". The final published `content` is unaffected either way, since
 * this module only ever informs how text is *displayed*.
 */

export type ContentFormatForLiveEditor = 'djot' | 'asciidoc';

export interface LiveInlineToken {
  text: string;
  className?: string;
}

export interface LiveLineInfo {
  lineClassName: string;
  tokens: LiveInlineToken[];
}

/** Style for markup delimiters/markers themselves (heading `#`, list `-`, `*`/`_`/`` ` `` wrappers, …) — present, but visually de-emphasized so they read as "syntax" rather than "content". Colour only, so metrics are untouched. */
const MARKER_CLASS = 'text-muted-foreground/50';

interface InlineRule {
  regex: RegExp;
  /** Builds the full run of tokens for a match — delimiters de-emphasized, content styled. Must return tokens whose text concatenates back to `m[0]` exactly. */
  build: (m: RegExpExecArray) => LiveInlineToken[];
}

/** A symmetric `delimiter + content + delimiter` run (bold, italic, code, …): delimiters get `MARKER_CLASS`, only the inner content gets `innerClass`. */
function wrap(delimOpen: string, content: string, delimClose: string, innerClass?: string): LiveInlineToken[] {
  return [
    { text: delimOpen, className: MARKER_CLASS },
    { text: content, className: innerClass },
    { text: delimClose, className: MARKER_CLASS },
  ];
}

const LINK_CLASS = 'text-primary underline underline-offset-2';
const WIKILINK_CLASS = 'text-primary underline decoration-dotted underline-offset-2';
// Background + radius only — no horizontal padding, which would shift
// every following character and change where the line wraps.
const HIGHLIGHT_CLASS = 'bg-yellow-200/60 dark:bg-yellow-500/30 rounded';
const CODE_CLASS = 'bg-muted rounded';

function inlineRules(format: ContentFormatForLiveEditor): InlineRule[] {
  const common: InlineRule[] = [
    { regex: /`([^`]+)`/g, build: (m) => wrap('`', m[1], '`', CODE_CLASS) },
    { regex: /\*([^*]+)\*/g, build: (m) => wrap('*', m[1], '*', 'font-bold') },
    { regex: /_([^_]+)_/g, build: (m) => wrap('_', m[1], '_', 'italic') },
    // Superscript/subscript are only marked by their delimiters here:
    // `align-super`/`align-sub` plus a smaller font-size would both grow
    // the line box and shift the caret.
    { regex: /\^([^^]+)\^/g, build: (m) => wrap('^', m[1], '^') },
    { regex: /~([^~]+)~/g, build: (m) => wrap('~', m[1], '~') },
    { regex: /\[\[([^\]]+)\]\]/g, build: (m) => wrap('[[', m[1], ']]', WIKILINK_CLASS) },
  ];

  if (format === 'djot') {
    return [
      ...common,
      { regex: /\{-([^{}]+)-\}/g, build: (m) => wrap('{-', m[1], '-}', 'line-through text-muted-foreground') },
      { regex: /\{=([^{}]+)=\}/g, build: (m) => wrap('{=', m[1], '=}', HIGHLIGHT_CLASS) },
      {
        // [text](url) — the visible link text is styled; the url and
        // brackets/parens are markup, so they stay de-emphasized.
        regex: /\[([^\]]*)\]\(([^)]*)\)/g,
        build: (m) => [
          { text: '[', className: MARKER_CLASS },
          { text: m[1], className: LINK_CLASS },
          { text: '](', className: MARKER_CLASS },
          { text: m[2], className: MARKER_CLASS },
          { text: ')', className: MARKER_CLASS },
        ],
      },
      {
        // [text][ref] — same idea for reference-style links.
        regex: /\[([^\]]*)\]\[([^\]]*)\]/g,
        build: (m) => [
          { text: '[', className: MARKER_CLASS },
          { text: m[1], className: LINK_CLASS },
          { text: '][', className: MARKER_CLASS },
          { text: m[2], className: MARKER_CLASS },
          { text: ']', className: MARKER_CLASS },
        ],
      },
    ];
  }

  return [
    ...common,
    { regex: /#([^#]+)#/g, build: (m) => wrap('#', m[1], '#', HIGHLIGHT_CLASS) },
    {
      // link:url[text] / image:url[alt] — only the bracketed text/alt is styled.
      regex: /(link|image):(\S+)\[([^\]]*)\]/g,
      build: (m) => [
        { text: `${m[1]}:`, className: MARKER_CLASS },
        { text: m[2], className: MARKER_CLASS },
        { text: '[', className: MARKER_CLASS },
        { text: m[3], className: LINK_CLASS },
        { text: ']', className: MARKER_CLASS },
      ],
    },
  ];
}

/**
 * Splits a single line's content (no `\n`, always) into a flat, ordered
 * list of runs covering every character of `text` exactly once —
 * `tokens.map(t => t.text).join('')` always equals `text`. Delimiters
 * are their own de-emphasized tokens, separate from the styled content
 * they wrap (see module doc comment).
 */
export function tokenizeInline(text: string, format: ContentFormatForLiveEditor): LiveInlineToken[] {
  if (text === '') return [];
  const rules = inlineRules(format);
  const tokens: LiveInlineToken[] = [];
  let i = 0;

  while (i < text.length) {
    let bestMatch: RegExpExecArray | undefined;
    let bestRule: InlineRule | undefined;

    for (const rule of rules) {
      rule.regex.lastIndex = i;
      const m = rule.regex.exec(text);
      if (!m) continue;
      if (
        !bestMatch ||
        m.index < bestMatch.index ||
        (m.index === bestMatch.index && m[0].length > bestMatch[0].length)
      ) {
        bestMatch = m;
        bestRule = rule;
      }
    }

    if (!bestMatch || !bestRule) {
      tokens.push({ text: text.slice(i) });
      break;
    }

    if (bestMatch.index > i) {
      tokens.push({ text: text.slice(i, bestMatch.index) });
    }

    tokens.push(...bestRule.build(bestMatch));
    i = bestMatch.index + bestMatch[0].length;
  }

  return tokens;
}

function isFenceMarkerLine(trimmed: string, format: ContentFormatForLiveEditor): boolean {
  if (format === 'djot') return /^```+$/.test(trimmed);
  return /^-{4,}$/.test(trimmed) || /^\.{4,}$/.test(trimmed);
}

// Colour only. The shared surface is already monospace, and a smaller
// font-size or `whitespace-pre` here would break caret alignment.
const FENCE_CLASS = 'bg-muted/70 text-muted-foreground';

/**
 * Classifies every line of `text` (in document order, tracking multi-line
 * state like open code fences) into block-level styling + tokenized
 * inline content. Always returns exactly `text.split('\n').length`
 * entries, 1:1 with the lines `LiveEditor` renders.
 */
export function classifyLines(text: string, format: ContentFormatForLiveEditor): LiveLineInfo[] {
  const lines = text.split('\n');
  const result: LiveLineInfo[] = [];
  let inFence = false;

  for (const rawLine of lines) {
    const trimmed = rawLine.trim();

    if (isFenceMarkerLine(trimmed, format)) {
      inFence = !inFence;
      result.push({ lineClassName: FENCE_CLASS, tokens: [{ text: rawLine }] });
      continue;
    }

    if (inFence) {
      result.push({ lineClassName: FENCE_CLASS, tokens: [{ text: rawLine }] });
      continue;
    }

    // Headings are bolded but NOT enlarged — the `#`/`=` marker count
    // already conveys the level, and changing font-size would desync the
    // caret for the rest of the document.
    const headingRe = format === 'djot' ? /^(#{1,6})(\s+)(.*)$/ : /^(={1,6})(\s+)(.*)$/;
    const heading = rawLine.match(headingRe);
    if (heading) {
      result.push({
        lineClassName: 'font-bold',
        tokens: [
          { text: heading[1] + heading[2], className: MARKER_CLASS },
          ...tokenizeInline(heading[3], format),
        ],
      });
      continue;
    }

    if (format === 'djot') {
      const blockquote = rawLine.match(/^(>\s?)(.*)$/);
      if (blockquote) {
        result.push({
          lineClassName: 'italic text-muted-foreground',
          tokens: [{ text: blockquote[1], className: MARKER_CLASS }, ...tokenizeInline(blockquote[2], format)],
        });
        continue;
      }

      const task = rawLine.match(/^(-\s+\[[ xX]\]\s+)(.*)$/);
      if (task) {
        result.push({
          lineClassName: '',
          tokens: [{ text: task[1], className: MARKER_CLASS }, ...tokenizeInline(task[2], format)],
        });
        continue;
      }

      const bullet = rawLine.match(/^(-\s+)(.*)$/);
      if (bullet) {
        result.push({
          lineClassName: '',
          tokens: [{ text: bullet[1], className: MARKER_CLASS }, ...tokenizeInline(bullet[2], format)],
        });
        continue;
      }

      const ordered = rawLine.match(/^(\d+\.\s+)(.*)$/);
      if (ordered) {
        result.push({
          lineClassName: '',
          tokens: [{ text: ordered[1], className: MARKER_CLASS }, ...tokenizeInline(ordered[2], format)],
        });
        continue;
      }

      if (/^([*\-_] ?){3,}$/.test(trimmed) && trimmed.length > 0) {
        result.push({
          lineClassName: 'text-muted-foreground/50',
          tokens: [{ text: rawLine }],
        });
        continue;
      }
    } else {
      const admonition = rawLine.match(/^(NOTE|TIP|IMPORTANT|WARNING|CAUTION)(:\s*)(.*)$/);
      if (admonition) {
        result.push({
          lineClassName: '',
          tokens: [
            { text: admonition[1], className: 'font-bold text-blue-600 dark:text-blue-400' },
            { text: admonition[2], className: MARKER_CLASS },
            ...tokenizeInline(admonition[3], format),
          ],
        });
        continue;
      }

      const bullet = rawLine.match(/^(\*\s+)(.*)$/);
      if (bullet) {
        result.push({
          lineClassName: '',
          tokens: [{ text: bullet[1], className: MARKER_CLASS }, ...tokenizeInline(bullet[2], format)],
        });
        continue;
      }

      const ordered = rawLine.match(/^(\.\s+)(.*)$/);
      if (ordered) {
        result.push({
          lineClassName: '',
          tokens: [{ text: ordered[1], className: MARKER_CLASS }, ...tokenizeInline(ordered[2], format)],
        });
        continue;
      }

      if (trimmed === "'''") {
        result.push({
          lineClassName: 'text-muted-foreground/50',
          tokens: [{ text: rawLine }],
        });
        continue;
      }
    }

    if (trimmed.startsWith('|')) {
      result.push({ lineClassName: FENCE_CLASS, tokens: [{ text: rawLine }] });
      continue;
    }

    result.push({ lineClassName: '', tokens: tokenizeInline(rawLine, format) });
  }

  return result;
}
